import type { Page } from "@aurumiq/cms-schema";
import { Button } from "@aurumiq/ui/components/button";
import { Tabs, TabsList, TabsTrigger } from "@aurumiq/ui/components/tabs";
import { cn } from "@aurumiq/ui/lib/utils";
import { ExternalLink, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { env } from "@/lib/env";
import type { CmsPreviewToken } from "@/lib/types";

const DEVICES = { mobile: 390, tablet: 820, desktop: 0 } as const;
export type Device = keyof typeof DEVICES;

export const DRAFT_MESSAGE = "aurumiq:cms:draft";
export const READY_MESSAGE = "aurumiq:cms:preview-ready";

/**
 * Landing-site preview iframe. The iframe loads the draft-mode URL from the preview token
 * (`/api/draft?token=…&slug=…` → redirects to `/preview/{slug}`), then receives the live page
 * over `postMessage({type:"aurumiq:cms:draft", page}, LANDING_ORIGIN)` on every debounced change.
 */
export function PreviewPane({
  token,
  page,
  onRefreshToken,
  className,
}: {
  token: CmsPreviewToken | null;
  /** Validated page or null when the draft does not parse (last good render stays). */
  page: Page | null;
  onRefreshToken: () => void;
  className?: string;
}) {
  const [device, setDevice] = useState<Device>("desktop");
  const [ready, setReady] = useState(false);
  const iframe = useRef<HTMLIFrameElement>(null);
  const latest = useRef(page);
  latest.current = page;

  const send = useCallback(() => {
    const win = iframe.current?.contentWindow;
    if (!win || !latest.current) return;
    win.postMessage({ type: DRAFT_MESSAGE, page: latest.current }, env.landingUrl);
  }, []);

  // The frame announces itself once mounted; push the current draft right away.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== env.landingUrl) return;
      if ((e.data as { type?: string })?.type === READY_MESSAGE) {
        setReady(true);
        send();
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [send]);

  // Debounced push on every change (`page` is read through the ref but must retrigger the effect).
  // biome-ignore lint/correctness/useExhaustiveDependencies: page drives the debounce
  useEffect(() => {
    if (!ready) return;
    const t = setTimeout(send, 400);
    return () => clearTimeout(t);
  }, [page, ready, send]);

  const width = DEVICES[device];
  return (
    <div className={cn("flex min-h-[60vh] flex-col gap-2", className)}>
      <div className="flex flex-wrap items-center gap-2">
        <Tabs value={device} onValueChange={(v) => setDevice(v as Device)}>
          <TabsList>
            <TabsTrigger value="mobile">Mobile</TabsTrigger>
            <TabsTrigger value="tablet">Tablet</TabsTrigger>
            <TabsTrigger value="desktop">Desktop</TabsTrigger>
          </TabsList>
        </Tabs>
        <span className="text-muted-foreground text-xs">
          {ready ? "live" : token ? "connecting…" : "no token"}
        </span>
        <div className="flex-1" />
        <Button variant="ghost" size="sm" onClick={onRefreshToken} aria-label="Refresh preview">
          <RefreshCw />
        </Button>
        {token ? (
          <Button asChild variant="outline" size="sm">
            <a href={token.url} target="_blank" rel="noreferrer">
              Open <ExternalLink />
            </a>
          </Button>
        ) : null}
      </div>
      <div className="flex-1 overflow-auto rounded-xl border bg-muted p-2">
        {token ? (
          <iframe
            ref={iframe}
            title="Landing preview"
            src={token.url}
            onLoad={() => setReady(false)}
            className="mx-auto h-full min-h-[60vh] bg-white shadow"
            style={{ width: width ? `${width}px` : "100%", maxWidth: "100%" }}
          />
        ) : (
          <div className="grid h-full min-h-[60vh] place-items-center text-muted-foreground text-sm">
            Requesting a preview token…
          </div>
        )}
      </div>
    </div>
  );
}
